import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { Send, HelpCircle, Mail } from "lucide-react";
import { toast } from "sonner";

const FAQS = [
  {
    question: "How do I accept a tour booking?",
    answer: "When you receive a tour offer, you'll see it in your notifications. Navigate to the booking details and click 'Accept' to confirm the booking. You'll then have access to customer details and can add the event to your calendar."
  },
  {
    question: "How do I get paid for completed tours?",
    answer: "Payments are processed after each completed tour. Make sure your payment details are up to date in the Payment section. Earnings are transferred to your preferred payment method within 3-5 business days after tour completion."
  },
  {
    question: "Can I decline a tour offer?",
    answer: "Yes, you can decline tour offers. However, consistently declining offers may affect your visibility on the platform. If you're unavailable for an extended period, consider using the 'Mark me inactive' toggle in Settings."
  },
  {
    question: "How do I update my availability?",
    answer: "You can mark yourself as inactive in Settings or from the header toggle. When inactive, you won't receive new tour offers. Remember to turn this off when you're ready to accept bookings again."
  },
  {
    question: "What happens if I need to cancel a confirmed tour?",
    answer: "Please contact Rahe Portal support immediately if you need to cancel. Frequent cancellations may affect your rating and standing on the platform. We recommend keeping your calendar up to date to avoid conflicts."
  },
  {
    question: "How are customer reviews handled?",
    answer: "After each tour, customers can leave a review and rating. These appear in your Reviews section and contribute to your overall rating. High ratings help you get more tour offers."
  },
  {
    question: "What languages does the platform support?",
    answer: "The Rahe Portal is available in English, Italian, Spanish, French, German, and Chinese. You can change your language preference in Settings."
  },
  {
    question: "How do I download my association letter?",
    answer: "Once your profile is complete, you can download your Rahe Portal Association Letter from the Profile page. Look for the 'Download Association Letter' button in the top right corner."
  },
  {
    question: "What should I do if I have issues with a customer?",
    answer: "Use the messaging feature to communicate professionally with customers. For serious issues, contact Rahe Portal support using the form below. We're here to help resolve any disputes."
  },
  {
    question: "How can I improve my visibility on the platform?",
    answer: "Complete your profile fully, maintain high ratings, respond promptly to tour offers, and be reliable. Guides with complete profiles and good reviews get priority in tour assignments."
  }
];

export default function HelpPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({ subject: "", message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const { data } = await api.get("/profile");
      setProfile(data);
    } catch {
      // Silently fail
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.subject || !form.message) {
      toast.error("Please fill in all fields");
      return;
    }
    
    setSending(true);
    try {
      await api.post("/help/contact", {
        name: profile?.firstName || user?.firstName || "",
        email: profile?.email || user?.email || "",
        subject: form.subject,
        message: form.message
      });
      toast.success("Message sent successfully! We'll get back to you soon.");
      setForm({ subject: "", message: "" });
    } catch {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="animate-slideUp space-y-6 max-w-4xl" data-testid="help-page">
      <p className="text-sm text-slate-500">Find answers to common questions or contact our support team</p>

      {/* FAQs */}
      <div className="bg-white border border-slate-100 rounded-xl shadow-sm p-6">
        <div className="flex items-center gap-2 mb-4">
          <HelpCircle className="w-5 h-5 text-blue-600" />
          <h3 className="text-lg font-semibold text-slate-900">Frequently Asked Questions</h3>
        </div>
        <Accordion type="single" collapsible className="w-full">
          {FAQS.map((faq, i) => (
            <AccordionItem key={i} value={`item-${i}`} data-testid={`faq-${i}`}>
              <AccordionTrigger className="text-left text-sm font-medium text-slate-700 hover:text-slate-900">
                {faq.question}
              </AccordionTrigger>
              <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                {faq.answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>

      {/* Contact Form */}
      <div className="bg-white border border-slate-100 rounded-xl shadow-sm p-6">
        <div className="flex items-center gap-2 mb-4">
          <Mail className="w-5 h-5 text-blue-600" />
          <h3 className="text-lg font-semibold text-slate-900">Contact Support</h3>
        </div>
        <p className="text-sm text-slate-500 mb-6">
          Can't find what you're looking for? Send us a message and we'll get back to you as soon as possible.
        </p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-500">Your Name</Label>
              <Input
                value={profile?.firstName || user?.firstName|| ""}
                disabled
                className="mt-1.5 bg-slate-50 text-slate-600"
                data-testid="help-name"
              />
            </div>
            <div>
              <Label className="text-slate-500">Your Email</Label>
              <Input
                value={profile?.email || user?.email || ""}
                disabled
                className="mt-1.5 bg-slate-50 text-slate-600"
                data-testid="help-email"
              />
            </div>
          </div>
          <div>
            <Label>Subject *</Label>
            <Input
              value={form.subject}
              onChange={(e) => setForm(f => ({ ...f, subject: e.target.value }))}
              placeholder="What's this about?"
              className="mt-1.5"
              data-testid="help-subject"
            />
          </div>
          <div>
            <Label>Message *</Label>
            <Textarea
              value={form.message}
              onChange={(e) => setForm(f => ({ ...f, message: e.target.value }))}
              placeholder="Describe your issue or question..."
              className="mt-1.5 min-h-[120px]"
              data-testid="help-message"
            />
          </div>
          <Button
            type="submit"
            disabled={sending}
            className="bg-[#1A1B2E] hover:bg-[#2D2E45]"
            data-testid="help-submit"
          >
            <Send className="w-4 h-4 mr-2" />
            {sending ? "Sending..." : "Send Message"}
          </Button>
        </form>
      </div>
    </div>
  );
}
